import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import AutoCamera from "../components/AutoCamera";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

export default function Attendance() {

  const navigate = useNavigate();

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const processingRef = useRef(false);
  const lastEmployeeRef = useRef(null);
  const cooldownRef = useRef(false);

  useEffect(() => {
    setRunning(true);
  }, []);

  useEffect(() => {
    if (result) {
      setTimeout(() => {
        const box = document.getElementById("resultBox");
        box?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 350);
    }
  }, [result]);

  const handleFrame = useCallback(async (file) => {

    if (!running || processingRef.current) return;

    // ❌ block if cooldown active
    if (cooldownRef.current) return;

    processingRef.current = true;

    try {
      const formData = new FormData();
      formData.append("file", file);

      // 🔥 timezone fix
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      formData.append("timezone", timezone);

      const res = await axios.post(`${API}/recognize-face`, formData);

      if (
        res.data.status === "Matched" ||
        res.data.status === "Already Marked"
      ) {
        const currentEmp = res.data.employee_id;

        // ❌ Same person → ignore
        if (lastEmployeeRef.current === currentEmp) {
          processingRef.current = false;
          return;
        }

        // ✅ New person → process
        lastEmployeeRef.current = currentEmp;

        setResult(res.data);

        // activate cooldown
        cooldownRef.current = true;

        setTimeout(() => {
          cooldownRef.current = false;
          lastEmployeeRef.current = null;
          setResult(null);
        }, 15000); // 15 sec
      }

    } catch (err) {

      const errorData = err.response?.data;
      const msg = errorData?.detail;

      // No face → just show message
      if (msg === "No face detected") {
        setResult({ message: " No face detected" });
        return;
      }

      // Multiple faces
      if (msg === "Multiple faces detected") {
        setResult({ message: "⚠️ Multiple faces detected" });
        return;
      }

      // ❌ Not registered → REDIRECT
      // ❌ Not registered → REDIRECT
      // ❌ Not registered → REDIRECT
      if (msg === "Employee not registered") {
        setResult({ message: "Face not registered" });

        setRunning(false);

        setTimeout(() => {
          navigate("/register", {
            state: { message: "Employee not registered" }
          });
        }, 1000);

        return;
      }

      // ⚠️ No faces in DB
      if (msg === "No faces registered") {
        setResult({ message: " No faces in system" });

        setRunning(false);

        setTimeout(() => {
          navigate("/register", {
            state: { message: "No employees registered yet" }
          });
        }, 1000);

        return;
      }
    
  } finally {
    processingRef.current = false;
  }

}, [running, navigate]);
return (
  <div
    className="
        min-h-screen
        bg-gradient-to-br from-[#0a0f1c] via-[#0f172a] to-[#020617]
        flex justify-center items-start
        p-6
      "
  >

    <div
      className="
          w-full max-w-xl
          bg-white/5 backdrop-xl
          border border-white/10
          rounded-2xl shadow-xl
          px-6 py-8
          flex flex-col items-center gap-6
          mt-10
        "
    >

      <h2 className="text-3xl font-bold text-white tracking-wide">
        Face Recognition System
      </h2>

      <div
        className="
            relative w-full max-w-[480px] aspect-square
            rounded-2xl overflow-hidden
            border border-blue-400/30
            shadow-xl bg-black
          "
      >

        <AutoCamera running={running} onFrame={handleFrame} />

        {running && (
          <div className="absolute inset-x-0 top-0 h-1 bg-blue-500 animate-scan"></div>
        )}

      </div>

      {result && (
        <>
          {(result.status === "Matched" || result.status === "Already Marked") ? (
            <div
              id="resultBox"
              className="w-full bg-green-500/10 border border-green-500/30 p-6 rounded-xl"
            >
              <h3 className="font-bold text-green-400 mb-2">
                ✅ Face Recognized
              </h3>

              <p className="text-white">Name: {result.name}</p>
              <p className="text-white">Code: {result.employee_code}</p>
              <p className="text-white">
                Time: {new Date().toLocaleString()}
              </p>
            </div>
          ) : (
            <div className="text-red-400 text-center mt-3">
              {result.message}
            </div>
          )}
        </>
      )}

    </div>

  </div>
);
}